import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  getQuote, 
  generateQuote, 
  updateQuoteStatus, 
  uploadPOD,
  deleteQuoteRequest,
  deleteAdditionalInvoice,
  syncACIPaymentStatus,
  getDriverJobSheetPdf,
  getStoredToken,
  archiveQuote,
  downloadInvoicePdf,
  openInvoicePdf,
  printInvoicePdf
} from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import GoldShieldLogo from "@/components/GoldShieldLogo";
import { 
  ArrowLeft,
  MapPin,
  Package,
  Clock,
  User,
  Building,
  Mail,
  Phone,
  Send,
  Archive,
  CheckCircle2,
  Upload,
  Loader2,
  FileText,
  Printer,
  Share2,
  Download,
  MessageCircle,
  FileImage,
  Trash2,
  Shield,
  AlertTriangle,
  RefreshCw,
  PoundSterling,
  LogOut
} from "lucide-react";
import { cn } from "@/lib/utils";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const statusLabels = {
  new: "Nou",
  quoted: "Cotat",
  paid: "Plătit",
  collected: "Preluat",
  delivered: "Livrat"
};

const statusColors = {
  new: "badge-new",
  quoted: "badge-quoted", 
  paid: "badge-paid",
  collected: "badge-collected",
  delivered: "badge-delivered"
};

export default function AdminRequestDetail() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [podFile, setPodFile] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Additional Charges Invoice state
  const [showACIModal, setShowACIModal] = useState(false);
  const [aciLoading, setAciLoading] = useState(false);
  const [additionalInvoices, setAdditionalInvoices] = useState([]);
  const [aciAmount, setAciAmount] = useState("");
  const [aciReason, setAciReason] = useState("");
  const [aciCategory, setAciCategory] = useState("other");

  const { register, handleSubmit, formState: { errors } } = useForm();

  const formatTermsTimestamp = (value) => {
    if (!value) return "N/A";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toISOString().replace("T", " ").slice(0, 16) + " UTC";
  };

  const fetchQuote = async () => {
    try {
      const data = await getQuote(quoteId);
      setQuote(data);
      // Fetch additional invoices for paid/collected/delivered orders
      if (data.status === 'paid' || data.status === 'collected' || data.status === 'delivered' || data.payment_status === 'paid') {
        fetchAdditionalInvoices();
      }
    } catch (error) {
      console.error("Error fetching quote:", error);
      toast.error("Nu s-a putut încărca cererea");
    } finally {
      setLoading(false);
    }
  };

  const fetchAdditionalInvoices = async () => {
    try {
      const token = getStoredToken();
      const response = await axios.get(`${API_URL}/api/admin/requests/${quoteId}/additional-invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdditionalInvoices(response.data);
    } catch (error) {
      console.error("Error fetching additional invoices:", error);
    }
  };

  const handleCreateAdditionalInvoice = async () => {
    if (!aciAmount || parseFloat(aciAmount) <= 0) {
      toast.error("Introdu o sumă validă");
      return;
    }
    if (!aciReason) {
      toast.error("Selectează motivul din lista T&C");
      return;
    }

    setAciLoading(true);
    try {
      const token = getStoredToken();
      const response = await axios.post(
        `${API_URL}/api/admin/requests/${quoteId}/additional-invoice`,
        {
          amount: parseFloat(aciAmount),
          reason_text: aciReason,
          category: aciCategory
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const invoiceNumber = response.data.additional_invoice_number;
      const emailStatus = response.data.email_status;
      
      if (emailStatus === "sent") {
        toast.success(`Factura ${invoiceNumber} a fost creată și trimisă clientului!`);
      } else if (emailStatus === "failed") {
        toast.warning(`Factura ${invoiceNumber} a fost creată, dar emailul nu a putut fi trimis. Poți retrimite manual.`);
      } else {
        toast.success(`Factura ${invoiceNumber} a fost creată!`);
      }
      
      setShowACIModal(false);
      setAciAmount("");
      setAciReason("");
      setAciCategory("other");
      fetchAdditionalInvoices();
    } catch (error) {
      console.error("Error creating additional invoice:", error);
      const errorMessage = error.response?.data?.detail || "Eroare la crearea facturii";
      const statusCode = error.response?.status;
      
      // Handle authentication errors - redirect to login
      if (statusCode === 401 || errorMessage.toLowerCase().includes("authentication")) {
        toast.error("Sesiunea a expirat. Te rugăm să te autentifici din nou.");
        // Clear invalid token and redirect to login
        localStorage.removeItem('rts_admin_token');
        setTimeout(() => {
          window.location.href = '/admin/login';
        }, 1500);
        return;
      }
      
      // More specific error messages
      if (errorMessage.includes("not found")) {
        toast.error("Cererea nu a fost găsită. Reîncarcă pagina.");
      } else if (errorMessage.includes("status")) {
        toast.error("Factura poate fi emisă doar pentru cereri plătite/colectate/livrate.");
      } else if (errorMessage.includes("PDF")) {
        toast.error("Eroare la generarea PDF-ului. Încearcă din nou.");
      } else if (errorMessage.includes("Database")) {
        toast.error("Eroare la salvarea în baza de date. Încearcă din nou.");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setAciLoading(false);
    }
  };

  const handleDownloadACI = async (invoiceId, invoiceNumber) => {
    try {
      const token = getStoredToken();
      const response = await axios.get(
        `${API_URL}/api/admin/additional-invoices/${invoiceId}/pdf`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error("Eroare la descărcarea PDF");
    }
  };

  const handleResendACI = async (invoiceId) => {
    try {
      const token = getStoredToken();
      await axios.post(
        `${API_URL}/api/admin/additional-invoices/${invoiceId}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email retrimis!");
      fetchAdditionalInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la retrimiterea emailului");
    }
  };

  const handleDeleteACI = async (invoiceId, invoiceNumber) => {
    if (!confirm(`Ești sigur că vrei să ștergi factura ${invoiceNumber}? Această acțiune nu poate fi anulată.`)) {
      return;
    }
    
    try {
      await deleteAdditionalInvoice(invoiceId);
      toast.success(`Factura ${invoiceNumber} a fost ștearsă`);
      fetchAdditionalInvoices();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Eroare la ștergerea facturii";
      toast.error(errorMessage);
    }
  };

  const handleSyncACIPayment = async (invoiceId, invoiceNumber) => {
    try {
      const result = await syncACIPaymentStatus(invoiceId);
      if (result.status === 'paid') {
        toast.success(`${invoiceNumber} marcat ca PLĂTIT!`);
        // Force refresh the additional invoices list with new key to trigger re-render
        setRefreshKey(prev => prev + 1);
        await fetchAdditionalInvoices();
      } else {
        toast.info(result.message);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Eroare la sincronizare";
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    fetchQuote();
  }, [quoteId]);

  const onSubmitQuote = async (data) => {
    setSubmitting(true);
    try {
      await generateQuote(quoteId, parseFloat(data.price));
      toast.success("Oferta a fost trimisă!");
      fetchQuote();
    } catch (error) {
      console.error("Error generating quote:", error);
      toast.error(error.response?.data?.detail || "Eroare la trimiterea ofertei");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await updateQuoteStatus(quoteId, newStatus);
      toast.success(`Status actualizat: ${statusLabels[newStatus]}`);
      fetchQuote();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Eroare la actualizarea statusului");
    }
  };

  const handlePODUpload = async () => {
    if (!podFile) {
      toast.error("Selectează un fișier POD");
      return;
    }
    
    setUploading(true);
    try {
      await uploadPOD(quoteId, podFile);
      toast.success("POD încărcat cu succes! Livrare confirmată.");
      setPodFile(null);
      fetchQuote();
    } catch (error) {
      console.error("Error uploading POD:", error);
      toast.error("Eroare la încărcarea POD");
    } finally {
      setUploading(false);
    }
  };

  const handleDriverJobSheet = async () => {
    try {
      const pdfBlob = await getDriverJobSheetPdf(quoteId);
      const blobUrl = window.URL.createObjectURL(new Blob([pdfBlob], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `driver_job_sheet_${quoteId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Driver job sheet generated.");
    } catch (error) {
      toast.error("Failed to generate driver job sheet.");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Sigur vrei să ștergi această cerere? Această acțiune este permanentă!")) {
      return;
    }
    
    try {
      await deleteQuoteRequest(quoteId);
      toast.success("Cererea a fost ștearsă");
      navigate("/admin");
    } catch (error) {
      console.error("Error deleting quote:", error);
      toast.error(error.response?.data?.detail || "Eroare la ștergerea cererii");
    }
  };

  const handleArchive = async () => {
    if (!window.confirm("Sigur vrei să arhivezi această cerere?")) {
      return;
    }
    
    try {
      await archiveQuote(quoteId);
      toast.success("Cererea a fost arhivată");
      navigate("/admin");
    } catch (error) {
      console.error("Error archiving quote:", error);
      toast.error(error.response?.data?.detail || "Eroare la arhivarea cererii");
    }
  };

  // Invoice functions for OWNER on paid requests
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  const handleViewInvoice = async () => {
    setInvoiceLoading(true);
    try {
      await openInvoicePdf(quoteId);
    } catch (error) {
      console.error("Error viewing invoice:", error);
      toast.error("Eroare la deschiderea facturii");
    }
    setInvoiceLoading(false);
  };

  const handlePrintInvoice = async () => {
    setInvoiceLoading(true);
    try {
      await printInvoicePdf(quoteId);
      toast.success("Factura se pregătește pentru printare...");
    } catch (error) {
      console.error("Error printing invoice:", error);
      toast.error("Eroare la printarea facturii");
    }
    setInvoiceLoading(false);
  };

  const handleDownloadInvoice = async () => {
    setInvoiceLoading(true);
    try {
      const blob = await downloadInvoicePdf(quoteId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${quote.invoice_number || quoteId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Factura descărcată");
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Eroare la descărcarea facturii");
    }
    setInvoiceLoading(false);
  };

  const handleShareWhatsApp = async () => {
    setInvoiceLoading(true);
    try {
      const blob = await downloadInvoicePdf(quoteId);
      const invoiceNumber = quote.invoice_number || quoteId;
      const message = encodeURIComponent(`Factura ${invoiceNumber} - RTS EXPRESS LTD\nVă trimitem factura pentru transport.`);
      
      // Try native share if available (mobile)
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], `Invoice-${invoiceNumber}.pdf`, { type: 'application/pdf' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `Factura ${invoiceNumber}`,
            text: `Factura pentru transport - RTS EXPRESS`,
            files: [file]
          });
          toast.success("Factura partajată");
          setInvoiceLoading(false);
          return;
        }
      }
      
      // Fallback to WhatsApp web with text message
      const whatsappUrl = `https://wa.me/?text=${message}`;
      window.open(whatsappUrl, '_blank');
      toast.info("Descarcă factura și atașeaz-o în WhatsApp");
    } catch (error) {
      console.error("Error sharing to WhatsApp:", error);
      toast.error("Eroare la partajarea facturii");
    }
    setInvoiceLoading(false);
  };

  const handleShareEmail = () => {
    const invoiceNumber = quote.invoice_number || quoteId;
    const subject = encodeURIComponent(`Factura ${invoiceNumber} - RTS EXPRESS LTD`);
    const body = encodeURIComponent(`Bună ziua,\n\nVă trimitem atașat factura ${invoiceNumber} pentru serviciile de transport.\n\nCu stimă,\nRTS EXPRESS LTD`);
    const mailtoUrl = `mailto:${quote.email || ''}?subject=${subject}&body=${body}`;
    window.open(mailtoUrl, '_blank');
    toast.info("Descarcă factura și atașeaz-o în email");
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      toast.error("Partajarea nu este suportată pe acest dispozitiv");
      return;
    }
    
    setInvoiceLoading(true);
    try {
      const blob = await downloadInvoicePdf(quoteId);
      const invoiceNumber = quote.invoice_number || quoteId;
      const file = new File([blob], `Invoice-${invoiceNumber}.pdf`, { type: 'application/pdf' });
      
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `Factura ${invoiceNumber} - RTS EXPRESS`,
          text: `Factura pentru transport`,
          files: [file]
        });
        toast.success("Factura partajată");
      } else {
        await navigator.share({
          title: `Factura ${invoiceNumber} - RTS EXPRESS`,
          text: `Factura pentru transport - descarcă din Admin Panel`
        });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error sharing:", error);
        toast.error("Eroare la partajare");
      }
    }
    setInvoiceLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#22c55e] animate-spin" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Cererea nu a fost găsită</h1>
          <Link to="/admin" data-testid="admin-detail-not-found-back-link">
            <Button data-testid="admin-detail-not-found-back-button">Înapoi la Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="bg-[#0f172a] border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" data-testid="admin-detail-back-link">
              <Button
                variant="ghost"
                className="text-slate-400 hover:text-white"
                data-testid="admin-detail-back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <GoldShieldLogo className="w-10 h-12" />
              <div>
                <h1 className="text-lg font-bold text-white">Detalii Cerere</h1>
                <p className="text-sm text-slate-400">#{quote.id.substring(0, 8)}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Archive button: OWNER can archive any request, STAFF can only archive unpaid */}
            {!quote.archived && (quote.status !== 'paid' || admin?.role === 'owner') && (
              <Button 
                onClick={handleArchive}
                variant="outline"
                className="border-amber-700 text-amber-400 hover:bg-amber-900/20"
                data-testid="admin-detail-archive-button"
              >
                <Archive className="w-4 h-4 mr-2" />
                Arhivează
              </Button>
            )}
            {/* Delete button: OWNER can delete any request, STAFF can only delete unpaid */}
            {(quote.status !== 'paid' || admin?.role === 'owner') && (
              <Button 
                onClick={handleDelete}
                variant="outline"
                className="border-red-900 text-red-400 hover:bg-red-900/20"
                data-testid="admin-detail-delete-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Șterge Cererea
              </Button>
            )}
            <Button
              onClick={async () => {
                await logout();
                toast.success("Deconectat cu succes");
                navigate("/admin/login", { replace: true });
              }}
              variant="ghost"
              className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white mb-1">
            DETALII <span className="text-[#22c55e]">CERERE</span>
          </h1>
          <p className="text-slate-400 font-mono">{quoteId}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Info */}
            <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6">
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#22c55e]" />
                Date Client
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Building className="w-4 h-4 text-slate-500" />
                  <div>
                    <p className="text-xs text-slate-500">Companie</p>
                    <p className="text-white">{quote.company_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-slate-500" />
                  <div>
                    <p className="text-xs text-slate-500">Contact</p>
                    <p className="text-white">{quote.contact_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <div>
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="text-white">{quote.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-slate-500" />
                  <div>
                    <p className="text-xs text-slate-500">Telefon</p>
                    <p className="text-white">{quote.phone}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Route Info - Full Addresses */}
            <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6">
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#3b82f6]" />
                Route & Addresses
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Pickup Address */}
                <div className="bg-slate-900 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-[#22c55e]/10 rounded-full flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-[#22c55e]" />
                    </div>
                    <p className="text-[#22c55e] font-bold uppercase text-xs tracking-wide">Pickup Address</p>
                  </div>
                  {quote.pickup_address ? (
                    <div className="space-y-1 text-sm" data-testid="admin-detail-pickup-address">
                      {quote.pickup_address.company_name && (
                        <p className="text-white font-bold">{quote.pickup_address.company_name}</p>
                      )}
                      {(quote.pickup_address.contact_name || quote.pickup_address.contact_phone) && (
                        <p className="text-slate-300">
                          {[quote.pickup_address.contact_name, quote.pickup_address.contact_phone].filter(Boolean).join(" — ")}
                        </p>
                      )}
                      {quote.pickup_address.address_line_1 && (
                        <p className="text-slate-300">{quote.pickup_address.address_line_1}</p>
                      )}
                      {quote.pickup_address.address_line_2 && (
                        <p className="text-slate-300">{quote.pickup_address.address_line_2}</p>
                      )}
                      {(quote.pickup_address.city || quote.pickup_address.county) && (
                        <p className="text-slate-300">
                          {[quote.pickup_address.city, quote.pickup_address.county].filter(Boolean).join(", ")}
                        </p>
                      )}
                      {quote.pickup_address.postcode && (
                        <p className="text-white font-bold">{quote.pickup_address.postcode}</p>
                      )}
                      {quote.pickup_address.country && (
                        <p className="text-slate-300">{quote.pickup_address.country}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-white font-bold text-lg" data-testid="admin-detail-pickup-postcode">{quote.pickup_postcode}</p>
                  )}
                </div>

                {/* Delivery Address */}
                <div className="bg-slate-900 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-[#3b82f6]/10 rounded-full flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-[#3b82f6]" />
                    </div>
                    <p className="text-[#3b82f6] font-bold uppercase text-xs tracking-wide">Delivery Address</p>
                  </div>
                  {quote.delivery_address ? (
                    <div className="space-y-1 text-sm" data-testid="admin-detail-delivery-address">
                      {quote.delivery_address.company_name && (
                        <p className="text-white font-bold">{quote.delivery_address.company_name}</p>
                      )}
                      {(quote.delivery_address.contact_name || quote.delivery_address.contact_phone) && (
                        <p className="text-slate-300">
                          {[quote.delivery_address.contact_name, quote.delivery_address.contact_phone].filter(Boolean).join(" — ")}
                        </p>
                      )}
                      {quote.delivery_address.address_line_1 && (
                        <p className="text-slate-300">{quote.delivery_address.address_line_1}</p>
                      )}
                      {quote.delivery_address.address_line_2 && (
                        <p className="text-slate-300">{quote.delivery_address.address_line_2}</p>
                      )}
                      {(quote.delivery_address.city || quote.delivery_address.county) && (
                        <p className="text-slate-300">
                          {[quote.delivery_address.city, quote.delivery_address.county].filter(Boolean).join(", ")}
                        </p>
                      )}
                      {quote.delivery_address.postcode && (
                        <p className="text-white font-bold">{quote.delivery_address.postcode}</p>
                      )}
                      {quote.delivery_address.country && (
                        <p className="text-slate-300">{quote.delivery_address.country}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-white font-bold text-lg" data-testid="admin-detail-delivery-postcode">{quote.delivery_postcode}</p>
                  )}
                </div>
              </div>

            </div>

            {/* Legal Acceptance */}
            <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6">
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#a855f7]" />
                Legal Acceptance
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Terms accepted</p>
                  <p className="text-white" data-testid="admin-detail-terms-accepted">
                    {quote.terms_accepted ? "Yes" : "No"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Accepted at (UTC)</p>
                  <p className="text-white" data-testid="admin-detail-terms-accepted-at">
                    {formatTermsTimestamp(quote.terms_accepted_at)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Client IP</p>
                  <p className="text-white" data-testid="admin-detail-terms-accepted-ip">
                    {quote.terms_accepted_ip || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Terms version</p>
                  <p className="text-white" data-testid="admin-detail-terms-version">
                    {quote.terms_version || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Cargo Info */}
            <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6">
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-[#f59e0b]" />
                Marfă
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Tip</p>
                  <p className="text-white capitalize">
                    {quote.cargo_type}
                    {quote.pallet_count && ` (${quote.pallet_count} palet${quote.pallet_count > 1 ? 'i' : ''})`}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Greutate</p>
                  <p className="text-white">{quote.total_weight_kg} kg</p>
                </div>
                {quote.dimensions && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Dimensiuni</p>
                    <p className="text-white">{quote.dimensions}</p>
                  </div>
                )}
              </div>
              {quote.additional_notes && (
                <div className="mt-4 pt-4 border-t border-slate-800">
                  <p className="text-xs text-slate-500 mb-1">Note Adiționale</p>
                  <p className="text-slate-300">{quote.additional_notes}</p>
                </div>
              )}
            </div>

            {/* Timing */}
            <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6">
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-400" />
                Timing
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Gata pentru Preluare</p>
                  <p className="text-white">{new Date(quote.ready_time).toLocaleString('ro-RO')}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Deadline Livrare</p>
                  <p className="text-white">{new Date(quote.delivery_deadline).toLocaleString('ro-RO')}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Urgență</p>
                  <p className={cn(
                    "capitalize font-medium",
                    quote.urgency === "immediate" && "text-red-400",
                    quote.urgency === "same_day" && "text-yellow-400",
                    quote.urgency === "overnight" && "text-blue-400"
                  )}>
                    {quote.urgency.replace('_', ' ')}
                  </p>
                </div>
              </div>
            </div>

            {/* POD Image */}
            {quote.pod_image && (
              <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6">
                <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <FileImage className="w-5 h-5 text-[#22c55e]" />
                  Proof of Delivery (POD)
                </h2>
                <img 
                  src={quote.pod_image} 
                  alt="Proof of Delivery" 
                  className="w-full max-w-md rounded-sm border border-slate-800"
                />
              </div>
            )}
          </div>

          {/* Right Column - Actions */}
          <div className="space-y-6">
            {/* Current Price with VAT breakdown */}
            <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6">
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-4 text-center">Prețuri</p>
              {quote.quoted_price ? (
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400">Net (fără TVA):</span>
                    <span className="text-white font-medium">£{(quote.quoted_price_net || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400">TVA 20%:</span>
                    <span className="text-white font-medium">£{(quote.quoted_price_vat || 0).toFixed(2)}</span>
                  </div>
                  <div className="border-t border-slate-700 pt-3 flex justify-between items-center">
                    <span className="text-white font-bold">TOTAL:</span>
                    <span className="font-['Barlow_Condensed'] text-3xl font-black text-[#22c55e]">
                      £{quote.quoted_price.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 text-center mt-2">Clientul plătește TOTAL</p>
                </div>
              ) : (
                <p className="text-slate-500 text-lg text-center">Necotată</p>
              )}
            </div>

            {/* Driver Job Sheet */}
            <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6">
              <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-white mb-3">
                Driver Job Sheet
              </h3>
              <p className="text-sm text-slate-400 mb-4">
                Generate a printable job sheet for the driver (no pricing).
              </p>
              <button
                onClick={handleDriverJobSheet}
                data-testid="admin-driver-job-sheet-btn"
                className="w-full bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Generate Driver Job Sheet PDF
              </button>
            </div>

            {/* Generate Quote Form */}
            {quote.status === "new" && (
              <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6">
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-white mb-4">
                  Generează Ofertă
                </h3>
                <form onSubmit={handleSubmit(onSubmitQuote)} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-slate-400">Preț NET (£) - fără TVA</Label>
                    <Input
                      {...register("price", { 
                        required: "Prețul este obligatoriu",
                        min: { value: 1, message: "Prețul minim este £1" }
                      })}
                      type="number"
                      step="0.01"
                      placeholder="Ex: 100.00"
                      className="bg-slate-900 border-slate-800 text-white text-lg font-bold"
                      data-testid="price-input"
                    />
                    {errors.price && (
                      <p className="text-red-400 text-xs">{errors.price.message}</p>
                    )}
                    <p className="text-xs text-slate-500">
                      TVA 20% se calculează automat.<br/>
                      Ex: £100 NET → £20 TVA → £120 TOTAL
                    </p>
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#22c55e] text-[#020617] hover:bg-[#22c55e]/90 font-bold"
                    data-testid="send-quote-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Se trimite...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Trimite Oferta
                      </>
                    )}
                  </Button>
                </form>
              </div>
            )}

            {/* Invoice Section - OWNER ONLY for paid/collected/delivered requests */}
            {admin?.role === 'owner' && (quote.status === "paid" || quote.status === "collected" || quote.status === "delivered") && (
              <div className="bg-[#0f172a] border border-[#22c55e]/30 rounded-sm p-6">
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-[#22c55e] mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Factură Client
                </h3>
                <p className="text-slate-400 text-sm mb-4">
                  {quote.invoice_number ? `Factură: ${quote.invoice_number}` : 'Vizualizează și partajează factura trimisă clientului'}
                </p>
                
                {/* Main View Invoice Button */}
                <Button
                  onClick={handleViewInvoice}
                  disabled={invoiceLoading}
                  className="w-full bg-[#22c55e] text-black hover:bg-[#22c55e]/90 font-bold mb-4"
                  data-testid="view-invoice-btn"
                >
                  {invoiceLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="mr-2 h-4 w-4" />
                  )}
                  Deschide Factura
                </Button>
                
                {/* Action Buttons Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {/* Print */}
                  <Button
                    onClick={handlePrintInvoice}
                    disabled={invoiceLoading}
                    variant="outline"
                    className="border-slate-700 text-slate-300 hover:bg-slate-800 text-xs px-2"
                    data-testid="print-invoice-btn"
                  >
                    <Printer className="w-4 h-4 mr-1" />
                    Print
                  </Button>
                  
                  {/* Download */}
                  <Button
                    onClick={handleDownloadInvoice}
                    disabled={invoiceLoading}
                    variant="outline"
                    className="border-slate-700 text-slate-300 hover:bg-slate-800 text-xs px-2"
                    data-testid="download-invoice-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Descarcă
                  </Button>
                  
                  {/* WhatsApp */}
                  <Button
                    onClick={handleShareWhatsApp}
                    disabled={invoiceLoading}
                    variant="outline"
                    className="border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366]/10 text-xs px-2"
                    data-testid="whatsapp-invoice-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-1" />
                    WhatsApp
                  </Button>
                  
                  {/* Email */}
                  <Button
                    onClick={handleShareEmail}
                    disabled={invoiceLoading}
                    variant="outline"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 text-xs px-2"
                    data-testid="email-invoice-btn"
                  >
                    <Mail className="w-4 h-4 mr-1" />
                    Email
                  </Button>
                </div>
                
                {/* Native Share Button (for mobile) */}
                {typeof navigator !== 'undefined' && navigator.share && (
                  <Button
                    onClick={handleNativeShare}
                    disabled={invoiceLoading}
                    variant="outline"
                    className="w-full mt-3 border-slate-700 text-slate-300 hover:bg-slate-800"
                    data-testid="share-invoice-btn"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Partajează prin alte aplicații
                  </Button>
                )}
              </div>
            )}

            {/* Status Actions */}
            {quote.status === "paid" && (
              <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6">
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-white mb-4">
                  Actualizează Status
                </h3>
                <Button
                  onClick={() => handleStatusUpdate("collected")}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold"
                  data-testid="mark-collected-btn"
                >
                  <Package className="mr-2 h-4 w-4" />
                  Marchează ca Preluat
                </Button>
              </div>
            )}

            {quote.status === "collected" && (
              <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6">
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-white mb-4">
                  Încarcă POD & Finalizează
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-slate-400">
                      Poză / Semnătură POD
                    </Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setPodFile(e.target.files[0])}
                      className="bg-slate-900 border-slate-800 text-white"
                      data-testid="pod-file-input"
                    />
                    {podFile && (
                      <p className="text-slate-400 text-xs">{podFile.name}</p>
                    )}
                  </div>
                  <Button
                    onClick={handlePODUpload}
                    disabled={uploading || !podFile}
                    className="w-full bg-[#22c55e] text-[#020617] hover:bg-[#22c55e]/90 font-bold"
                    data-testid="upload-pod-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Se încarcă...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Încarcă POD & Confirmă Livrare
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {quote.status === "delivered" && (
              <div className="bg-[#22c55e]/10 border border-[#22c55e]/20 rounded-sm p-6 text-center">
                <CheckCircle2 className="w-12 h-12 text-[#22c55e] mx-auto mb-3" />
                <p className="font-['Barlow_Condensed'] text-xl font-bold text-[#22c55e]">
                  LIVRARE COMPLETĂ
                </p>
                <p className="text-slate-400 text-sm mt-2">
                  POD încărcat și email trimis clientului
                </p>
              </div>
            )}

            {/* WhatsApp Info */}
            {quote.whatsapp_visible && (
              <div className="bg-[#25D366]/10 border border-[#25D366]/20 rounded-sm p-4">
                <p className="text-sm text-slate-300">
                  <strong className="text-[#25D366]">WhatsApp Activ</strong><br/>
                  Clientul poate contacta șoferul prin WhatsApp.
                </p>
              </div>
            )}

            {/* Additional Charges Invoice Section - Only for PAID requests */}
            {(quote.status === "paid" || quote.status === "collected" || quote.status === "delivered") && (
              <div className="bg-[#0f172a] border border-amber-600/30 rounded-sm p-6">
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-amber-500 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Taxe Suplimentare (T&C)
                </h3>
                <p className="text-slate-400 text-sm mb-4">
                  Emite factură pentru taxe suplimentare în cazul nerespectării Termenilor și Condițiilor.
                </p>
                <Button
                  onClick={() => setShowACIModal(true)}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold"
                  data-testid="additional-charges-btn"
                >
                  <PoundSterling className="mr-2 h-4 w-4" />
                  Additional Charges Invoice
                </Button>

                {/* List existing additional invoices */}
                {additionalInvoices.length > 0 && (
                  <div className="mt-4 space-y-2" key={`aci-list-${refreshKey}`}>
                    <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Facturi emise:</p>
                    {additionalInvoices.map((inv) => (
                      <div key={`${inv.id}-${inv.payment_status}`} className="bg-slate-900 border border-slate-700 rounded p-3 flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium text-sm">{inv.additional_invoice_number}</p>
                          <p className="text-slate-400 text-xs">£{inv.total_amount?.toFixed(2)} • {inv.category?.replace('_', ' ')}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={cn(
                              "text-xs px-1.5 py-0.5 rounded",
                              inv.email_status === "sent" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
                            )}>
                              {inv.email_status === "sent" ? "Email Trimis" : "Email Eșuat"}
                            </span>
                            <span className={cn(
                              "text-xs px-1.5 py-0.5 rounded",
                              inv.payment_status === "paid" ? "bg-green-500/20 text-green-400" : "bg-amber-500/20 text-amber-400"
                            )}>
                              {inv.payment_status === "paid" ? "Plătit" : "Neplătit"}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownloadACI(inv.id, inv.additional_invoice_number)}
                            className="text-slate-400 hover:text-white"
                            title="Descarcă PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {inv.email_status !== "sent" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleResendACI(inv.id)}
                              className="text-amber-500 hover:text-amber-400"
                              title="Retrimite email"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Delete button - only for unpaid invoices */}
                          {inv.payment_status !== "paid" && (
                            <>
                              {/* Sync payment status button */}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleSyncACIPayment(inv.id, inv.additional_invoice_number)}
                                className="text-blue-500 hover:text-blue-400 hover:bg-blue-500/10"
                                title="Verifică statusul plății în Stripe"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteACI(inv.id, inv.additional_invoice_number)}
                                className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                title="Șterge factura"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Back Button */}
            <Link to="/admin" className="block" data-testid="admin-detail-back-bottom-link">
              <Button 
                variant="outline" 
                className="w-full border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
                data-testid="admin-detail-back-bottom-button"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Înapoi la Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Additional Charges Invoice Modal */}
      <Dialog open={showACIModal} onOpenChange={setShowACIModal}>
        <DialogContent className="bg-[#0f172a] border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-500 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Additional Charges Invoice
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Emite o factură pentru taxe suplimentare datorate nerespectării T&C.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-400">
                Suma (GBP) *
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={aciAmount}
                onChange={(e) => setAciAmount(e.target.value)}
                placeholder="ex: 50.00"
                className="bg-slate-900 border-slate-700 text-white"
                data-testid="aci-amount-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-400">
                Categorie
              </Label>
              <Select value={aciCategory} onValueChange={setAciCategory}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="overweight">Overweight</SelectItem>
                  <SelectItem value="oversize">Oversize</SelectItem>
                  <SelectItem value="waiting_time">Waiting Time</SelectItem>
                  <SelectItem value="re_attempt">Re-attempt</SelectItem>
                  <SelectItem value="incorrect_details">Incorrect Details</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-400">
                Motiv / Referință T&C *
              </Label>
              <Select value={aciReason} onValueChange={setAciReason}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="aci-reason-input">
                  <SelectValue placeholder="Selectează motivul din T&C" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 max-h-[300px]">
                  <SelectItem value="Section 8 – Waiting time: Additional charges for waiting time exceeding 15 minutes at collection/delivery location (£1/minute as per T&C Section 8).">
                    Section 8 – Waiting Time (£1/min after 15min)
                  </SelectItem>
                  <SelectItem value="Section 9 – Surcharges: Pass-through charges including Congestion Charge, ULEZ, tolls, parking fees, or access permits as per T&C Section 9.">
                    Section 9 – Surcharges (Congestion/ULEZ/Tolls/Parking)
                  </SelectItem>
                  <SelectItem value="Section 10 – Failed collection: Goods not available, access refused, or contact unavailable at agreed collection time as per T&C Section 10.">
                    Section 10 – Failed Collection
                  </SelectItem>
                  <SelectItem value="Section 10 – Redelivery/Return: Delivery could not be completed due to recipient unavailability or incorrect address as per T&C Section 10.">
                    Section 10 – Redelivery/Return Required
                  </SelectItem>
                  <SelectItem value="Section 10 – Incorrect weight/dimensions: Actual weight or dimensions differ materially from declared values as per T&C Section 10.">
                    Section 10 – Incorrect Weight/Dimensions
                  </SelectItem>
                  <SelectItem value="Section 10 – Unsafe loading/unloading: Site conditions were unsafe or illegal for loading/unloading as per T&C Section 10.">
                    Section 10 – Unsafe Loading/Unloading Conditions
                  </SelectItem>
                  <SelectItem value="Section 15 – Packaging: Damage caused by inadequate packaging, insufficient internal protection, or inappropriate palletisation as per T&C Section 15.">
                    Section 15 – Inadequate Packaging
                  </SelectItem>
                  <SelectItem value="Section 7 – Collection/Delivery: Delays or additional costs due to goods not being ready at agreed time, or lack of appropriate loading/unloading facilities as per T&C Section 7.">
                    Section 7 – Goods Not Ready / No Access
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Motivul selectat va apărea pe factură conform Termenilor și Condițiilor</p>
            </div>

            {aciAmount && parseFloat(aciAmount) > 0 && (
              <div className="bg-slate-800 rounded p-3">
                <p className="text-sm text-slate-400">Preview:</p>
                <p className="text-white">Sumă: £{parseFloat(aciAmount).toFixed(2)}</p>
                <p className="text-white">VAT (20%): £{(parseFloat(aciAmount) * 0.20).toFixed(2)}</p>
                <p className="text-[#22c55e] font-bold">Total: £{(parseFloat(aciAmount) * 1.20).toFixed(2)}</p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowACIModal(false)}
              className="border-slate-700 text-slate-400"
            >
              Anulează
            </Button>
            <Button
              onClick={handleCreateAdditionalInvoice}
              disabled={aciLoading || !aciAmount || !aciReason}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="aci-generate-btn"
            >
              {aciLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Se generează...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Generate & Send
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
